import { useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, Mail, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApi, useDirectory } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Section, KV, StatusBadge, Loading, ErrorBox } from "@/components/common/ui-bits";
import { CommentsBox } from "@/components/common/CommentsBox";
import { DocumentsBox } from "@/components/common/DocumentsBox";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { DateInput } from "@/components/common/DateInput";
import { fmtDate, fmtDateTime, TAX_STATUSES } from "@/lib/format";

function AddRow({ placeholder, onAdd, withDate, testid }) {
  const [v, setV] = useState("");
  const [d, setD] = useState("");
  return (
    <form className="mt-3 flex flex-wrap gap-2" onSubmit={(e) => { e.preventDefault(); if (v.trim() && (!withDate || d)) { onAdd(v.trim(), d); setV(""); setD(""); } }}>
      <input className="field-input min-w-0 flex-1" placeholder={placeholder} value={v} onChange={(e) => setV(e.target.value)} data-testid={`${testid}-input`} />
      {withDate && <DateInput className="w-40" placeholder="Fecha" value={d} onChange={setD} testid={`${testid}-date`} ariaLabel="Fecha" />}
      <Button size="icon" type="submit" variant="outline" aria-label="Agregar" data-testid={`${testid}-add`}><Plus className="h-4 w-4" /></Button>
    </form>
  );
}

export default function TaxCaseDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const internal = user.role !== "client";
  const { data: t, error, reload, setData } = useApi(`/tax-cases/${id}`);
  const { name } = useDirectory();
  const [askDocs, setAskDocs] = useState(false);
  if (error) return <ErrorBox text={error} />;
  if (!t) return <Loading />;

  const patch = async (data) => {
    try { const r = await api.patch(`/tax-cases/${id}`, data); setData(r.data); toast.success("Caso actualizado"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const pending = t.documents.filter((d) => d.status === "Pendiente").length;

  return (
    <div>
      <PageHeader back={internal ? "/taxes" : "/portal/taxes"} title={t.title} subtitle={`${t.number} · ${t.client_name}`}
        actions={internal && <>
          <select className="field-input w-56" value={t.status} onChange={(e) => patch({ status: e.target.value })} data-testid="tax-status-select" aria-label="Estado">
            {TAX_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
          <Button variant="outline" onClick={() => setAskDocs(true)} disabled={!pending} data-testid="request-documents-button"><Mail className="mr-2 h-4 w-4" />Solicitar documentos</Button>
        </>} />
      <p className="mb-6 rounded-md border border-slate-200 bg-white p-3 text-xs text-slate-500">Este módulo organiza documentos e información. No realiza cálculos de impuestos, no presenta declaraciones electrónicamente ni garantiza resultados.</p>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Section title="Resumen">
          <KV items={[["Estado", <StatusBadge key="s" status={t.status} testid="tax-status" />], ["Año fiscal", t.tax_year], ...(internal ? [["Responsable", name(t.responsible_id)]] : []), ["Documentos pendientes", pending]]} />
        </Section>
        <Section title="Documentos requeridos" className="lg:col-span-2" testid="tax-documents">
          <ul className="divide-y divide-slate-100">
            {t.documents.length === 0 && <li className="py-2 text-sm text-slate-400">Sin documentos definidos.</li>}
            {t.documents.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-3 py-2 text-sm" data-testid={`tax-doc-${d.id}`}>
                {d.status === "Recibido" ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-amber-500" />}
                <span className="min-w-0 flex-1">{d.name}{d.received_at && <span className="block text-xs text-slate-400">Recibido {fmtDateTime(d.received_at)}</span>}</span>
                <StatusBadge status={d.status} />
                {internal && <>
                  <Button size="sm" variant="ghost" data-testid={`tax-doc-toggle-${d.id}`}
                    onClick={() => patch({ documents: t.documents.map((x) => x.id === d.id ? { ...x, status: x.status === "Recibido" ? "Pendiente" : "Recibido" } : x) })}>
                    Marcar {d.status === "Recibido" ? "pendiente" : "recibido"}</Button>
                  <Button size="icon" variant="ghost" aria-label="Quitar" onClick={() => patch({ documents: t.documents.filter((x) => x.id !== d.id) })}><Trash2 className="h-4 w-4 text-slate-400" /></Button>
                </>}
              </li>
            ))}
          </ul>
          {internal && <AddRow testid="tax-doc-new" placeholder="Agregar documento requerido…" onAdd={(n) => patch({ documents: [...t.documents, { name: n, status: "Pendiente" }] })} />}
        </Section>
        <Section title="Checklist" testid="tax-checklist">
          <ul className="space-y-2">
            {t.checklist.length === 0 && <li className="text-sm text-slate-400">Sin elementos.</li>}
            {t.checklist.map((c) => (
              <li key={c.id}><label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={c.done} disabled={!internal} className="accent-blue-700" data-testid={`tax-check-${c.id}`}
                  onChange={() => patch({ checklist: t.checklist.map((x) => x.id === c.id ? { ...x, done: !x.done } : x) })} />
                <span className={c.done ? "text-slate-400 line-through" : ""}>{c.label}</span></label></li>
            ))}
          </ul>
          {internal && <AddRow testid="tax-check-new" placeholder="Nuevo elemento…" onAdd={(l) => patch({ checklist: [...t.checklist, { label: l, done: false }] })} />}
        </Section>
        <Section title="Fechas importantes" testid="tax-dates">
          <ul className="space-y-2 text-sm">
            {t.important_dates.length === 0 && <li className="text-slate-400">Sin fechas.</li>}
            {t.important_dates.map((d) => <li key={d.id} className="flex justify-between"><span>{d.label}</span><b>{fmtDate(d.date)}</b></li>)}
          </ul>
          {internal && <AddRow testid="tax-date-new" withDate placeholder="Descripción…" onAdd={(l, d) => patch({ important_dates: [...t.important_dates, { label: l, date: d }] })} />}
        </Section>
        {internal && (
          <Section title="Notas privadas (solo equipo)">
            <CommentsBox testid="tax-private-notes" comments={(t.private_notes || []).map((n) => ({ ...n, internal: true }))} placeholder="Nota privada…"
              onAdd={async (text) => { await api.post(`/tax-cases/${id}/notes`, { text }); reload(); }} />
          </Section>
        )}
        <Section title="Mensajes con el cliente" className="lg:col-span-2">
          <CommentsBox testid="tax-messages" comments={t.messages} placeholder="Escriba un mensaje…"
            onAdd={async (text) => { await api.post(`/tax-cases/${id}/messages`, { text }); reload(); }} />
        </Section>
        <Section title="Archivos del caso" className={internal ? "lg:col-span-3" : ""}>
          <DocumentsBox clientId={t.client_id} entityType="caso_fiscal" entityId={id} testid="tax-files" />
        </Section>
      </div>
      <ConfirmDialog open={askDocs} onOpenChange={setAskDocs} title="Solicitar documentos al cliente" confirmLabel="Enviar solicitud" testid="request-docs-confirm"
        description={`Se enviará un correo al cliente con ${pending} documento(s) pendiente(s) y se registrará el mensaje en el caso.`}
        onConfirm={async () => {
          try { const r = await api.post(`/tax-cases/${id}/request-documents`); toast.success(r.data.email_sent ? "Solicitud enviada por correo" : "Solicitud registrada (correo no enviado)"); setAskDocs(false); reload(); }
          catch (e) { toast.error(errMsg(e)); }
        }} />
    </div>
  );
}
